import { useRecoilValue, useRecoilState } from "recoil";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { authTokenState } from "../recoil/authAtom";
import { apiClient } from "../helpers/axiosClient";
import { useEffect, useState } from "react";
import { examinationTypesState } from "../recoil/examinationTypesAtom";
import Collapsible from "./Collapsible";
import { StarIcon } from "@heroicons/react/solid";
import { Tooltip as ReactTooltip } from "react-tooltip";
import SimilarCourses from "./SimilarCourses.jsx";
import { RATING_TOOLTIP_TEXTS } from "../../constants/ratingTooltips";
import ExerciseGroupDisclaimer from "../common/ExerciseGroupDisclaimer";
import { isExerciseGroup } from "../helpers/smartExerciseGroupHandler";
import ExamSchedule from "./ExamSchedule.jsx";
import { courseDetailsSemesterAtom } from "../recoil/courseDetailsSemesterAtom";
import { useDegradedMode } from "../common/useDegradedMode";
import DegradedPlaceholder from "../common/DegradedPlaceholder";

// Unified course data
import {
  selectedCourseInfoSelector,
  selectedCourseSemesterSelector,
  selectedSemesterSelector,
} from "../recoil/unifiedCourseDataSelectors";

// error handling
import { errorHandlingService } from "../errorHandling/ErrorHandlingService";

// The course sheet and the ExaminationTypes lookup are fetched independently, so
// the sheet can arrive first. Until the lookup lands — or if it has no entry for
// an id — the type cell has no name to show, but the part's remark and weightage
// are still worth rendering.
const UNKNOWN_EXAM_TYPE = "—";

export default function CourseInfo() {
  const selectedCourse = useRecoilValue(selectedCourseInfoSelector);
  const semesterAbbreviation = useRecoilValue(selectedCourseSemesterSelector);
  const selectedSemester = useRecoilValue(selectedSemesterSelector);
  const openedForSemester = useRecoilValue(courseDetailsSemesterAtom);
  const authToken = useRecoilValue(authTokenState);
  const { isDegradedMode } = useDegradedMode();
  const [examinationIdState, setExaminationIdState] = useRecoilState(
    examinationTypesState
  );
  const [examInformationState, setExamInformation] = useState(null);
  const [containsCourseRatings, setContainsCourseRatings] = useState(false);

  // fetch achievement parts information (will be done every time a course is selected)
  async function fetchCourseInformation(selectedCourse, authToken) {
    try {
      const res = await apiClient.get(
        `https://integration.unisg.ch/EventApi/CourseInformationSheets/latestPublishedByHsgEntityId/${selectedCourse.hsgEntityId}`,
        authToken,
        {
          headers: {
            "API-Version": "3",
          },
        }
      );

      return res.data;
    } catch (err) {
      try {
        const res = await apiClient.get(
          `https://integration.unisg.ch/EventApi/CourseInformationSheets/latestPublishedByHsgEntityId/${
            selectedCourse.courses?.[0]?.courseNumber ||
            selectedCourse.courseNumber
          }`,
          authToken,
          {
            headers: {
              "API-Version": "3",
            },
          }
        );

        return res.data;
      } catch (err) {
        console.log(err);
        errorHandlingService.handleError(err);
        return null;
      }
    }
  }

  useEffect(() => {
    if (selectedCourse && authToken) {
      fetchCourseInformation(selectedCourse, authToken).then((data) => {
        setExamInformation(data);
      });
    }
  }, [selectedCourse, authToken]);

  // fetch exam types (will be done only once)

  async function fetchExaminationIds() {
    try {
      const res = await apiClient.get(
        `https://integration.unisg.ch/AcametaApi/ExaminationTypes?fields=id,shortName,description`,
        authToken
      );

      const examinationIdObject = {};
      res.data.forEach((examinationId) => {
        examinationIdObject[examinationId.id] = {
          shortName: examinationId.shortName,
          description: examinationId.description,
        };
      });

      setExaminationIdState(examinationIdObject);
    } catch (err) {
      console.log(err);
      errorHandlingService.handleError(err);
    }
  }

  useEffect(() => {
    // The list is static, but this panel remounts on every tab visit — the atom
    // that caches it is what keeps the fetch to once per session.
    const alreadyLoaded =
      examinationIdState && Object.keys(examinationIdState).length > 0;
    if (authToken && !alreadyLoaded) {
      fetchExaminationIds();
    }
    //never include setters
    // eslint-disable-next-line
  }, [authToken]);

  // fetch ratings (will be done every time a course is selected)
  const [courseWithRatings, setCourseWithRatings] = useState(null);

  async function fetchCourseRatings(selectedCourse, authToken) {
    try {
      const res = await apiClient.get(
        `https://api.shsg.ch/course-ratings/by-course/${
          selectedCourse.courses?.[0]?.courseNumber ||
          selectedCourse.courseNumber
        }`,
        authToken
      );
      res.data = res.data.filter(
        (course) => course.courseNumber !== "7,721,1.00" // this has been requested to be removed
      );

      if (res.data.length > 0) {
        const ratingData = res.data[0];
        const updatedCourse = {
          ...selectedCourse,
          avgRatings: ratingData.avgRatings,
          nbOfRatings: ratingData.nbOfRatings,
          comments: ratingData.comments,
        };

        setCourseWithRatings(updatedCourse);
      }
    } catch (err) {
      console.log(err);
      errorHandlingService.handleError(err);
    }
  }

  useEffect(() => {
    // Ratings are SHSG-backed; skip the fetch while degraded (isDegradedMode
    // is a dep so recovery re-fetches automatically).
    if (isDegradedMode) {
      setCourseWithRatings(null);
      return;
    }
    if (selectedCourse && authToken) {
      // Reset courseWithRatings before fetching new ratings
      setCourseWithRatings(null);
      fetchCourseRatings(selectedCourse, authToken);
    }
  }, [selectedCourse, authToken, isDegradedMode]);

  useEffect(() => {
    if (courseWithRatings && courseWithRatings.avgRating === "N/A") {
      setContainsCourseRatings(false);
    } else {
      setContainsCourseRatings(true);
    }
  }, [courseWithRatings, selectedCourse, setCourseWithRatings]);

  if (!selectedCourse || selectedCourse.shortName === undefined) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg p-8 text-center text-gray-600 shadow-sm">
        <p className="text-lg font-semibold text-gray-800">
          Select a course to see its details
        </p>
        <p className="max-w-sm text-sm">
          Click any course in the list on the left — its ratings, exam format
          and description will show up here.
        </p>
      </div>
    );
  }

  // Not every catalog entry carries it (exercise groups, preview data), and a
  // missing one used to take the whole panel down.
  const achievementFormStatus = selectedCourse.achievementFormStatus ?? {};

  return (
    <>
      {/* // Course Name and Link to courses page and course info sheet // */}

      <div className="flex-col flex p-2 md:p-4 h-full text-gray-800 rounded-lg shadow-sm overflow-y-auto">
        {/* <ErrorComponent /> */}
        {/* Course title and key info */}
        <header className="font-bold lg:text-2xl">
          <div className="flex justify-between pb-4">
            <a
              href={
                selectedCourse?.courses?.[0]?.timeTableLink ||
                selectedCourse?.timeTableLink ||
                "#"
              }
              target="_blank"
              rel="noreferrer"
            >
              {selectedCourse.shortName}
            </a>
            {semesterAbbreviation && (
              <a
                href={`https://tools.unisg.ch/handlers/Public/CourseInformationSheet.ashx/semester/${semesterAbbreviation}/eventnumber/${
                  selectedCourse.courses?.[0]?.courseNumber ||
                  selectedCourse.courseNumber
                }.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLinkIcon className="h-6 w-6" />
              </a>
            )}
          </div>
        </header>

        {/* // ECTS / Classifications / Central? / Lecturers // */}

        <div className="overflow-auto scrollbar-hide overscroll-auto">
          <div className="text-xs font-semibold text-gray-700 lg:text-base">
            <div className="" label="credits and exam info">
              {(selectedCourse.credits / 100).toFixed(2)} ECTS |{" "}
              {selectedCourse.classification}{" "}
              {achievementFormStatus.isCentral &&
              achievementFormStatus.isDeCentral
                ? `| Central & Decentral (${achievementFormStatus.description})`
                : achievementFormStatus.isCentral
                ? `| Central (${achievementFormStatus.description})`
                : achievementFormStatus.isDeCentral
                ? `| Decentral (${achievementFormStatus.description})`
                : ""}
            </div>
            <div className="mb-4">
              {(selectedCourse.courses?.[0]?.lecturers ||
                selectedCourse.lecturers) &&
                (
                  selectedCourse.courses?.[0]?.lecturers ||
                  selectedCourse.lecturers
                )
                  .map((prof) => {
                    return prof.displayName;
                  })
                  .join(" • ")}
            </div>
          </div>

          {/* // Course Description // */}

          {isDegradedMode && selectedCourse && selectedCourse.shortName !== undefined && (
            <DegradedPlaceholder compact feature="Ratings" className="mb-4" />
          )}

          {courseWithRatings && containsCourseRatings && (
            <>
              {/* Exercise Group Disclaimer */}
              {isExerciseGroup(selectedCourse) && <ExerciseGroupDisclaimer />}
              
              {/* Course Ratings */}
              <div
                className="grid grid-cols-1 lg:grid-cols-5 gap-8 text-base pb-4 "
                key={courseWithRatings.shortName}
              >
              <div className="col-span-2">
                <dl className="">
                  <div className="flex text-sm font-medium toggle text-gray-900">
                    <StarIcon className="w-6 h-6 mr-1" color="#386641" />
                    {courseWithRatings.avgRating} ·{" "}
                    {courseWithRatings.nbOfRatings}{" "}
                    {courseWithRatings.nbOfRatings === 1 ? "Rating" : "Ratings"}
                  </div>
                  {Object.keys(courseWithRatings.avgRatings)
                    .filter((category) => category !== "professor")
                    .map((category) => (
                      <dt key={category}>
                        <div className="items-center flex flex-row md:">
                          <div
                            className="capitalize w-1/3  mr-4"
                            data-tooltip-id={`rating-${category}`}
                            data-tooltip-content={
                              RATING_TOOLTIP_TEXTS[category]
                            }
                          >
                            {category}
                          </div>
                          <ReactTooltip
                            id={`rating-${category}`}
                            place="top"
                            style={{
                              backgroundColor: "#f9fafb",
                              color: "#111827",
                              border: "1px solid #d1d5db",
                              borderRadius: "0.375rem",
                              fontSize: "0.875rem",
                            }}
                          />

                          <div
                            className="bg-gray-300 rounded-full h-3 align-middle flex-1 cursor-help"
                            data-tooltip-id={`rating-${category}`}
                            data-tooltip-content={
                              RATING_TOOLTIP_TEXTS[category]
                            }
                          >
                            {/*  dark:bg-gray-600 */}
                            <div
                              className="bg-gray-500 h-3 rounded-full "
                              //dark:bg-gray-300
                              style={{
                                width: `${
                                  courseWithRatings.avgRatings[category] * 20
                                }%`,
                              }}
                            />
                          </div>
                          <div
                            className="ml-4 w-1/6 cursor-help"
                            data-tooltip-id={`rating-${category}`}
                            data-tooltip-content={
                              RATING_TOOLTIP_TEXTS[category]
                            }
                          >
                            Ø {courseWithRatings.avgRatings[category]}
                          </div>
                        </div>
                      </dt>
                    ))}
                </dl>
              </div>
              {/* RATING COMMENTS */}
              {/* <div className="col-span-3 h-40 overflow-auto">
                            {courseWithRatings &&
                            "comments" in courseWithRatings &&
                            courseWithRatings.comments.length > 0 ? (
                                <dl className="space-y-2 ">
                                    {courseWithRatings.comments.map((comment) => (
                                        <div key={comment.text}>
                                            <dt className="">
                                                <h4 className="font-bold">
                                                    {comment.semester}
                                                </h4>
                                                <p className="">
                                                    {comment.text}
                                                </p>
                                            </dt>
                                        </div>
                                    ))}
                                </dl>
                            ) : (
                                <></>
                            )}
                        </div> */}
            </div>
            </>
          )}

          {/* // Exam Description // */}
          {/* Achievement parts is an empty array in events api --> new api call needed? TODO: */}
          <section className="pb-2">
            <h2 className="text-lg font-bold text-gray-700 ">
              Exam Information
            </h2>
            {/* A projected semester reuses its reference's cisId, so the
                lookup above can name HS26 while HS27 is on screen, or while
                the map opened the course from an HS27 card. Exam dates are
                judged on the semester the student is looking at; any
                mismatch shows none. */}
            <ExamSchedule
              course={selectedCourse}
              semester={
                semesterAbbreviation === selectedSemester &&
                (openedForSemester === null ||
                  openedForSemester === selectedSemester)
                  ? semesterAbbreviation
                  : null
              }
            />
            <div className="pb-1">
              {examInformationState ? (
                examInformationState.examinationParts.map((part, index) => {
                  return (
                    <div key={index} className="w-full text-sm">
                      <div className="grid grid-cols-3">
                        <div className="font-semibold">
                          {examinationIdState?.[part.examinationTypeId]
                            ?.shortName ?? UNKNOWN_EXAM_TYPE}
                        </div>
                        <div>{part.remark}</div>
                        <div className="text-left">{part.weightage / 100}%</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-base text-gray-700">
                  No exam breakdown available
                </div>
              )}
            </div>
          </section>
          {/* // General Course Description // */}
          <section className="flex flex-col flex-1" label="Course Information">
            <h2 className="flex-none pb-2 text-xl font-bold text-gray-700 ">
              Course Information
            </h2>

            <div className="flex-1 text-gray-700 rounded-lg">
              <Collapsible label="Learning Objectives" CloseOnToggle={false}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedCourse.learningObjectives,
                  }}
                />
              </Collapsible>
              <Collapsible label="Content" CloseOnToggle={true}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedCourse.courseContent,
                  }}
                />
              </Collapsible>
              <Collapsible label="Prerequisites" CloseOnToggle={true}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedCourse.coursePrerequisites,
                  }}
                />
              </Collapsible>

              <Collapsible label="Structure" CloseOnToggle={true}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedCourse.courseStructure,
                  }}
                />
              </Collapsible>
              <Collapsible label="Literature" CloseOnToggle={true}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedCourse.courseLiterature,
                  }}
                />
              </Collapsible>
              <Collapsible label="Additional Information" CloseOnToggle={true}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedCourse.courseAdditionalInformation,
                  }}
                />
              </Collapsible>
            </div>
          </section>
          <SimilarCourses selectedCourse={selectedCourse} />
        </div>
      </div>
    </>
  );
}

export { CourseInfo };
